import type { ActionLogFilters, ActionLogWindow } from './action-log-constants';

/**
 * Turning the filter bar into the query string both read endpoints accept.
 *
 * Written once and shared by the list and the export. They take the SAME contract on the server,
 * and the whole value of the export is that it is the thing on screen — a second serialiser would
 * be a second chance for the two to drift.
 *
 * The server rejects any parameter outside its list with a 400 rather than ignoring it, which is
 * the behaviour worth designing against: an unknown parameter here fails loudly instead of
 * producing an unfiltered list that looks filtered.
 */

/** Exactly the parameters the server implements. Anything else is a 400. */
export const ACTION_LOG_QUERY_PARAMS = [
	'from',
	'to',
	'decision',
	'actionType',
	'platform',
	'channelId',
	'ruleId',
	'search',
	'limit',
	'cursor'
] as const;

export type ActionLogQueryParam = (typeof ACTION_LOG_QUERY_PARAMS)[number];

export type ActionLogQuery = Partial<Record<ActionLogQueryParam, string>>;

export interface ActionLogQueryInput {
	/** Already resolved, so a relative preset cannot mean a different instant per request. */
	window: ActionLogWindow;
	filters: ActionLogFilters;
	/** List only. Omitted for the export, which is capped server-side instead. */
	limit?: number;
	cursor?: string | null;
}

/**
 * Comma-joined rather than repeated.
 *
 * The server accepts both conventions. One is picked so the serialised query is stable enough to
 * use as a cache key, and comma-joining keeps a URL with several decisions readable in a bug report.
 */
const joinMultiSelect = (values: readonly string[]): string | undefined =>
	values.length > 0 ? values.join(',') : undefined;

/**
 * Build the query.
 *
 * `from` and `to` are ALWAYS sent, even when they match the server's own default. The alternative
 * is that the server resolves "last 7 days" at its own clock a second or two after the browser did,
 * and the export lands on a marginally different window from the list beside it.
 */
export const toActionLogQueryParams = (input: ActionLogQueryInput): ActionLogQuery => {
	const { window, filters, limit, cursor } = input;

	const query: ActionLogQuery = {
		from: window.from.toISOString(),
		to: window.to.toISOString()
	};

	const decision = joinMultiSelect(filters.decisions);
	if (decision) query.decision = decision;

	const actionType = joinMultiSelect(filters.actionTypes);
	if (actionType) query.actionType = actionType;

	const channelId = joinMultiSelect(filters.channelIds);
	if (channelId) query.channelId = channelId;

	const search = filters.search.trim();
	if (search) query.search = search;

	if (limit !== undefined) query.limit = String(limit);
	// A cursor is only meaningful on a follow-up page; sending an empty one is a 400.
	if (cursor) query.cursor = cursor;

	return query;
};

/** The same query as a URL search string, for the export link and for cache keys. */
export const toActionLogSearchString = (input: ActionLogQueryInput): string =>
	new URLSearchParams(toActionLogQueryParams(input)).toString();
